import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { userApi, User } from "../../services/userApi";
import { OrdersList } from "../../components/admin/OrdersList";
import { useApi } from "../../context/ApiContext";
import { FaBox, FaCreditCard, FaEnvelope, FaPhone, FaUser, FaShoppingBag } from "react-icons/fa";
import { getTranslatedError } from "../../utils/errorUtils";

export const UserDetail: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { api } = useApi();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    if (userId) {
      fetchUser();
      fetchOrders();
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchOrders();
    }
  }, [page, userId]);

  const fetchUser = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const userData = await userApi.getUser(userId!);
      setUser(userData);
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.users.errors.fetch"));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (): Promise<void> => {
    try {
      setOrdersLoading(true);
      setOrdersError(null);
      const response = await userApi.getUserOrders(userId!, {
        page,
        limit,
      });
      setOrders(response.orders || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      setOrdersError(getTranslatedError(err, t, "admin.orders.errors.fetch"));
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string): Promise<void> => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      setOrders(
        orders.map((o) => (o._id === orderId ? { ...o, status: newStatus as any } : o))
      );
    } catch (err: any) {
      setOrdersError(getTranslatedError(err, t, "admin.orders.errors.updateStatus"));
    }
  };

  const getRoleLabel = (role: string): string => {
    return t(`admin.users.roles.${role}`) || role;
  };

  if (loading) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <Stack spacing="lg">
          <Alert variant="error">{t("admin.users.notFound")}</Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <div className="flex items-center justify-between">
          <Heading size="xl">{t("admin.users.userDetails")}</Heading>
          <Button variant="outline" onClick={() => navigate("/admin/users")}>
            {t("admin.users.backToUsers")}
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Card padding="lg">
              <Stack spacing="md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaUser className="text-2xl text-blue-600" />
                  </div>
                  <div>
                    <Heading size="md">{user.name}</Heading>
                    <p className="text-sm text-gray-500">{getRoleLabel(user.role)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {user.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <FaEnvelope className="text-gray-400" />
                      <span className="text-gray-600">{t("admin.users.email")}:</span>
                      <span className="font-medium">{user.email}</span>
                    </div>
                  )}
                  {user.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <FaPhone className="text-gray-400" />
                      <span className="text-gray-600">{t("admin.users.phone")}:</span>
                      <span className="font-medium">{user.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <FaUser className="text-gray-400" />
                    <span className="text-gray-600">{t("admin.users.username")}:</span>
                    <span className="font-medium">{user.username}</span>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="space-y-2">
                    {user.isBanned ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t("admin.users.accountStatus")}:</span>
                        <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                          {t("admin.users.banned")}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t("admin.users.status")}:</span>
                        {user.isApproved ? (
                          <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                            {t("admin.users.approved")}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800">
                            {t("admin.users.pendingApproval")}
                          </span>
                        )}
                      </div>
                    )}
                    {user.isBanned && user.bannedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t("admin.users.bannedAt")}:</span>
                        <span className="text-sm font-medium">
                          {new Date(user.bannedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {user.isBanned && user.bannedReason && (
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-600">{t("admin.users.bannedReason")}:</span>
                        <span className="text-sm font-medium text-right max-w-xs">
                          {user.bannedReason}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t("admin.users.emailVerification")}:</span>
                      {user.isVerified ? (
                        <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800">
                          {t("admin.users.verified")}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800">
                          {t("admin.users.notVerified")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t("admin.users.memberSince")}:</span>
                      <span className="text-sm font-medium">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {(user.role !== "admin" && user.role !== "store_admin") && (
                  <div className="pt-4 border-t">
                    <Heading size="sm" className="mb-3">
                      {t("admin.users.accountActions")}
                    </Heading>
                    <div className="space-y-2">
                      {user.isBanned ? (
                        <Button
                          variant="outline"
                          fullWidth
                          onClick={async () => {
                            if (window.confirm(t("admin.users.confirmUnban", { name: user.name }))) {
                              try {
                                const updatedUser = await userApi.unbanUser(user._id);
                                setUser(updatedUser);
                                setError(null);
                              } catch (err: any) {
                                setError(getTranslatedError(err, t, "admin.users.errors.unban"));
                              }
                            }
                          }}
                          className="justify-start text-green-600 hover:text-green-700"
                        >
                          {t("admin.users.unban")}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          fullWidth
                          onClick={async () => {
                            const reason = window.prompt(t("admin.users.banReasonPrompt"));
                            if (reason !== null) {
                              if (window.confirm(t("admin.users.confirmBan", { name: user.name }))) {
                                try {
                                  const updatedUser = await userApi.banUser(user._id, reason || undefined);
                                  setUser(updatedUser);
                                  setError(null);
                                } catch (err: any) {
                                  setError(getTranslatedError(err, t, "admin.users.errors.ban"));
                                }
                              }
                            }
                          }}
                          className="justify-start text-red-600 hover:text-red-700"
                        >
                          {t("admin.users.ban")}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <Heading size="sm" className="mb-3">
                    {t("admin.users.quickLinks")}
                  </Heading>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={() => {
                        const ordersSection = document.getElementById("user-orders");
                        ordersSection?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="justify-start"
                    >
                      <FaShoppingBag className="mr-2" />
                      {t("admin.users.viewOrders")} ({total})
                    </Button>
                    {user.email && (
                      <Button
                        variant="outline"
                        fullWidth
                        onClick={() => {
                          // Filter orders page by user email
                          navigate(`/admin/orders?search=${encodeURIComponent(user.email!)}`);
                        }}
                        className="justify-start"
                      >
                        <FaBox className="mr-2" />
                        {t("admin.users.allOrders")}
                      </Button>
                    )}
                    {user.email && (
                      <Button
                        variant="outline"
                        fullWidth
                        onClick={() => {
                          // Navigate to payments page filtered by user email
                          navigate(`/admin/payments?search=${encodeURIComponent(user.email!)}`);
                        }}
                        className="justify-start"
                      >
                        <FaCreditCard className="mr-2" />
                        {t("admin.users.payments")}
                      </Button>
                    )}
                  </div>
                </div>
              </Stack>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card padding="lg" id="user-orders">
              <Stack spacing="md">
                <Heading size="lg">{t("admin.users.orders")}</Heading>

                {ordersError && <Alert variant="error">{ordersError}</Alert>}

                <OrdersList
                  orders={orders}
                  loading={ordersLoading}
                  page={page}
                  total={total}
                  limit={limit}
                  onPageChange={setPage}
                  onStatusChange={handleStatusChange}
                  showUser={false}
                  showLocation={true}
                />
              </Stack>
            </Card>
          </div>
        </div>
      </Stack>
    </Container>
  );
};

export default UserDetail;
